import {
	Box,
	Stack,
	SvgIcon,
	Table,
	TableBody,
	TableCell,
	tableCellClasses,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
} from "@mui/material";
import { TTierCommission, ITiers } from "../../../../types/spiffSetup";
import { ReactComponent as InformationIcon } from "@/assets/icons/common/circled_information.svg";
import { styled } from "@mui/material/styles";
import { ReactNode } from "react";
import compact from "lodash/compact";
import { IOpCode } from "@/PayPlans/types/sections/technicians";
import { isNumber } from "lodash";

const StyledTableRow = styled(TableRow)(({ theme }) => ({
	"&:nth-of-type(odd)": {
		backgroundColor: theme.palette.action.hover,
	},
	"&:last-child td, &:last-child th": {
		border: 0,
	},
	"&:first-of-type": {
		backgroundColor: "#ffffff", // Add white background to the first row
	},
}));

const StyledTableCell = styled(TableCell)(() => ({
	[`&.${tableCellClasses.head}`]: {
		color: "#666666",
		fontSize: 14,
		height: 30,
		paddingTop: 0,
		paddingBottom: 0,
		paddingRight: 8,
		minWidth: 50,
		textAlign: "right",
		borderBottom: "none",
	},
	[`&.${tableCellClasses.body}`]: {
		minWidth: 50,
		color: "#666666",
		fontSize: 14,
		height: 20,
		paddingTop: 0,
		paddingBottom: 0,
		paddingRight: 8,
		borderBottom: "none",
	},
	[`&.${tableCellClasses.head}:not(:first-of-type):not(:last-child):not(:nth-of-type(2))`]: {
		borderLeft: `1.5px solid #999999`,
		borderRight: `1.5px solid #999999`,
	},
	[`&.${tableCellClasses.body}:not(:first-of-type):not(:last-child):not(:nth-of-type(2))`]: {
		borderLeft: `1.5px solid #999999`,
		borderRight: `1.5px solid #999999`,
	},
}));

const buildText = (value: number, unit: string) => {
	if (unit.toLowerCase() === "percent") {
		return `${getValue(value.toString(), 2)}%`;
	}
	if (unit.toLowerCase() === "dollar") {
		return `$${getValue(value.toString(), 2)}`;
	}
	if (unit.toLowerCase() === "dollar per unit") {
		return `$/u ${getValue(value.toString(), 2)}`;
	}
	if (unit.toLowerCase() === "unit") {
		return `${getValue(value.toString(), 2)}`;
	}
	return value;
};

const getHeaders = (commissions: TTierCommission[], headerTitle: string, asc: boolean): string[] => {
	const headers = commissions.map((commission, index) => {
		const { from, to } = commission;
		const [fromInf, toInf] = asc ? ["-Inf.", "Inf."] : ["Inf.", "-Inf."];
		const fromText = index === 0 ? fromInf : `${buildText(from.value, from.input)}`;
		const toText = index === commissions.length - 1 ? toInf : `${buildText(to.value, to.input)}`;

		return `${fromText} to ${toText}`;
	});

	return [headerTitle, ...headers];
};

const getUnit = (unit: string, svg?: boolean) => {
	if (unit.toLowerCase() === "dollar") {
		return "$";
	}
	if (unit.toLowerCase() === "percent") {
		return "%";
	}
	if (unit.toLowerCase() === "dollar per unit") {
		return "$/u";
	}
	if (unit.toLowerCase() === "dollar per day") {
		return "$/d";
	}
	if (unit.toLowerCase() === "duration" && svg) {
		return (
			<SvgIcon className="input-value-icon" sx={{ padding: "4px", fontSize: "22px" }}>
				<svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 13 13" fill="none">
					<path
						fillRule="evenodd"
						clipRule="evenodd"
						d="M6.5 0.975C3.44863 0.975 0.975 3.44863 0.975 6.5C0.975 9.55137 3.44863 12.025 6.5 12.025C9.55137
									 12.025 12.025 9.55137 12.025 6.5C12.025 3.44863 9.55137 0.975 6.5 0.975ZM0 6.5C0 2.91015 2.91015 0
									 6.5 0C10.0899 0 13 2.91015 13 6.5C13 10.0899 10.0899 13 6.5 13C2.91015 13 0 10.0899 0 6.5ZM6.175
									 2.1125C6.44424 2.1125 6.6625 2.33076 6.6625 2.6V6.21854L9.79633 8.02781C10.0295 8.16243 10.1094 8.46058
									 9.97478 8.69374C9.84016 8.92691 9.54201 9.00681 9.30884 8.87219L5.93125 6.92219C5.78042 6.83511 5.6875 6.67417
									 5.6875 6.5V2.6C5.6875 2.33076 5.90576 2.1125 6.175 2.1125Z"
						fill="white"
					/>
				</svg>
			</SvgIcon>
		);
	}
	return unit;
};

const getValue = (value: string, decimals: number) => {
	if (isNumber(value)) {
		return parseFloat(value).toFixed(decimals);
	}
	return value;
};

type cellValue = {
	unit: string;
	value: string;
};

const getRows = (tiers: ITiers[]) => {
	return tiers.map((tier, index) => {
		const { from, to, commissions } = tier;
		const fromCellValue: cellValue = {
			unit: "",
			value: index === 0 ? "-" : `${from.value}`,
		};
		const toCellValue: cellValue = {
			unit: "",
			value: index === tiers.length - 1 ? "-" : `${to.value}`,
		};
		const commissionRows = commissions.map((commissionItem) => {
			const { commission } = commissionItem;

			const cell: cellValue = {
				unit: commission.input.toLowerCase() !== "percent" ? (getUnit(commission.input) as string) : "%",
				value: commission.value.toString(),
			};

			return cell;
		});

		return [fromCellValue, toCellValue, ...commissionRows];
	});
};

const compactOpCodes = (selectedOperationCodes: IOpCode[]) => {
	if (!selectedOperationCodes.length) {
		return "";
	}
	const codes = selectedOperationCodes.map((sel: IOpCode) => `${sel.code} (${sel.type})`).join(", ");
	return codes;
};

interface IEligibilityTiersProps {
	tiers: ITiers[];
	tiersHeaderTitle: string;
	informationMessage: string[];
	commissionColumnsTitle: string;
	verticalText: ReactNode;
	verticalTextClassName?: string;
	franchises: string[];
	spiffType: string;
	selectedDepartment?: string[];
	selectedPayType?: string[];
	selectedOperationCodes?: IOpCode[];
	component?: string;
	asc?: boolean;
}

const interestSpiffTypes = ["GPS Compliance", "Trades"];

const EligibilityTiers = (props: IEligibilityTiersProps) => {
	const {
		tiers,
		verticalText,
		tiersHeaderTitle = "",
		commissionColumnsTitle,
		informationMessage = [],
		franchises,
		spiffType,
		verticalTextClassName = "!text-sm !text-default !font-montserrat !font-medium !mt-20 !-rotate-90",
		selectedDepartment,
		selectedPayType,
		selectedOperationCodes,
		component,
		asc = true,
	} = props;
	const commissionsTiersHeaders = getHeaders(tiers[0].commissions, tiersHeaderTitle, asc);
	const commissionTiersRows = getRows(tiers);
	const compactedInformationMessage = compact(informationMessage);

	return (
		<>
			{interestSpiffTypes.includes(spiffType) ? (
				<Box className="flex flex-row">
					<Typography className="!font-montserrat !font-medium text-gray-text">{franchises.join(", ")}</Typography>
				</Box>
			) : null}
			{component && (
				<Box className="flex flex-row">
					<Typography className="!font-montserrat !font-medium text-gray-text">{component}</Typography>
				</Box>
			)}
			{selectedDepartment && Boolean(selectedDepartment.length) && (
				<Box className="flex flex-row">
					<Typography className="!font-montserrat !font-medium text-gray-text">{selectedDepartment.join(", ")}</Typography>
				</Box>
			)}
			{selectedPayType && Boolean(selectedPayType.length) && selectedOperationCodes && Boolean(selectedOperationCodes.length) && (
				<Box className="flex flex-row">
					<Typography className="!font-montserrat !font-medium text-gray-text">
						{selectedPayType.join(", ")} Pay: {compactOpCodes(selectedOperationCodes)}
					</Typography>
				</Box>
			)}
			<Stack display="flex" direction="row" pr={2} mt={1}>
				<Typography className={verticalTextClassName} alignSelf="start">
					{verticalText}
				</Typography>
				<Stack display="flex" direction="row" alignItems="center" justifyContent="space-between">
					<TableContainer>
						<Table aria-label="customized table">
							<TableHead>
								<TableRow>
									<StyledTableCell colSpan={2} sx={{ width: 200 }}>
										<Box className="!text-base !text-default !font-montserrat !font-medium flex flex-col !justify-start !text-left">
											{commissionsTiersHeaders[0].split("\n").map((line: string, index: number) => (
												<div key={index}>{line}</div>
											))}
										</Box>
									</StyledTableCell>
									{commissionsTiersHeaders.slice(1).map((header, index) => {
										const headerKey = `${index}`;
										return (
											<StyledTableCell
												key={headerKey}
												className="!text-base !text-default !font-sans !font-normal"
												align="right"
												sx={{ width: 170 }}
											>
												{header}
											</StyledTableCell>
										);
									})}
								</TableRow>
							</TableHead>
							<TableBody>
								<StyledTableRow>
									<StyledTableCell></StyledTableCell>
									<StyledTableCell></StyledTableCell>
									{tiers[0].commissions.map((commission) => (
										<StyledTableCell key={commission._id} align="right" />
									))}
								</StyledTableRow>
								<StyledTableRow>
									<StyledTableCell className="!text-base !text-default !font-bold !font-sans">From</StyledTableCell>
									<StyledTableCell className="!text-base !text-default !font-bold !font-sans">To</StyledTableCell>
									{tiers[0].commissions.map((commission) => (
										<StyledTableCell
											key={commission._id}
											align="right"
											className="!text-base !text-default !font-bold !font-sans"
										>
											{commissionColumnsTitle}
										</StyledTableCell>
									))}
								</StyledTableRow>
								{commissionTiersRows.map((row, rowIndex) => {
									const rowKey = `${rowIndex}`;
									return (
										<StyledTableRow key={rowKey}>
											{row.map((cell, cellIndex) => {
												const cellKey = `${rowIndex}-${cellIndex}`;
												return (
													<StyledTableCell key={cellKey} align={cellIndex > 1 ? "right" : "left"}>
														{cellIndex <= 1 ? (
															<Box className="flex flex-row align-middle justify-start">
																<Typography className="!text-base !text-default !font-normal !font-sans">
																	{getValue(cell.value, 1)}
																</Typography>
															</Box>
														) : (
															<Box className="flex flex-row align-middle justify-end gap-4">
																<Typography className="!text-base !text-default !font-normal !font-sans">
																	{cell.unit.toLowerCase() === "%" ? "" : getUnit(cell.unit, true)}
																</Typography>
																<Typography className="!text-base !text-default !font-normal !font-sans !min-w-10">
																	{cell.unit.toLowerCase() !== "%"
																		? getValue(cell.value, 2)
																		: `${getValue(cell.value, 2)}${cell.unit}`}
																</Typography>
															</Box>
														)}
													</StyledTableCell>
												);
											})}
										</StyledTableRow>
									);
								})}
							</TableBody>
						</Table>
					</TableContainer>
				</Stack>
			</Stack>
			{!!compactedInformationMessage.length ? (
				<Box mt={1}>
					{compactedInformationMessage.map((informationMsg) => {
						return (
							<Stack display="flex" direction="row" alignItems="center" ml="35px" key={informationMsg}>
								<SvgIcon
									className="!w-[18px] !h-[18px] !fill-default !mt-[6px]"
									component={InformationIcon}
									viewBox="0 0 24 24"
								/>
								<Typography className="!text-sm !text-default !font-sans !font-normal">{informationMsg}</Typography>
							</Stack>
						);
					})}
				</Box>
			) : (
				<br />
			)}
		</>
	);
};

export default EligibilityTiers;
